import { useEffect, useState } from "react";
import { ExternalLink, MessageCircle, Save } from "lucide-react";
import Sheet from "./Sheet";
import ClientUsageDetail from "./ClientUsageDetail";
import ClientConfigForm from "./ClientConfigForm";
import ClientPayments from "./ClientPayments";
import { BRANDING_THEME as BRAND_COLORS } from "../../../constants/brandingTheme";
import { buildBusinessUrl, displayBusinessUrl } from "../../../utils/businessUrl";

const TABS = [
  { key: "uso", label: "Uso" },
  { key: "config", label: "Configuración" },
  { key: "pagos", label: "Pagos" },
];

/**
 * Detalle de un cliente (panel lateral en PC, sheet en móvil). Tres pestañas: lo que
 * se consulta seguido (Uso) primero; editar y cobrar un nivel más adentro.
 */
export default function ClientDetailPanel({
  client, open, onClose, side, period, usage, prevUsage, row, initialTab = "uso", waHref,
  editingData, setEditingData, paymentForm, setPaymentForm, payments,
  computePrice, getSubscriptionStatus, onSave, onApplyAddonsNow, onRegisterPayment,
}) {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { if (open) setTab(initialTab); }, [open, client?.id, initialTab]);

  if (!client) return null;
  const st = row?.st || getSubscriptionStatus(client.subscription?.expiresAt, client.subscription?.isActive);
  const conn = row?.conn || { waConnected: false, calendarsReady: 0, seats: 1 };

  const header = (
    <div className="sa-sheet-meta">
      <span className="sa-pill" style={{ "--pill": st.color }}>{st.label}{st.relative ? ` · ${st.relative}` : ""}</span>
      <span className="sa-meta-item"><span className="sa-dot" style={{ background: conn.waConnected ? BRAND_COLORS.primary.main : BRAND_COLORS.error }} />WhatsApp</span>
      <span className="sa-meta-item"><span className="sa-dot" style={{ background: conn.calendarsReady === 0 ? BRAND_COLORS.error : conn.calendarsReady >= conn.seats ? BRAND_COLORS.primary.main : BRAND_COLORS.warning }} />Calendar {conn.calendarsReady}/{conn.seats}</span>
      <a className="sa-meta-link" href={buildBusinessUrl(client.slug)} target="_blank" rel="noopener noreferrer">
        {displayBusinessUrl(client.slug)} <ExternalLink size={12} />
      </a>
      {waHref && (
        <a className="sa-btn sa-btn-sm sa-wa" href={waHref} target="_blank" rel="noopener noreferrer" title="Escribirle al WhatsApp conectado del negocio">
          <MessageCircle size={14} /> WhatsApp
        </a>
      )}
    </div>
  );

  const tabs = (
    <div className="sa-segment" role="tablist">
      {TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={tab === t.key}
          className={`sa-segment-btn ${tab === t.key ? "is-active" : ""}`}
          onClick={() => setTab(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  const footer = tab === "config" ? (
    <button type="button" className="sa-btn sa-btn-primary sa-btn-block" onClick={onSave}>
      <Save size={16} /> Guardar cambios
    </button>
  ) : null;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      side={side}
      title={client.business?.name}
      subtitle={`@${client.auth?.username}`}
      header={header}
      tabs={tabs}
      footer={footer}
    >
      {tab === "uso" && (
        <ClientUsageDetail clientId={client.id} period={period} current={usage} previous={prevUsage} compact />
      )}
      {tab === "config" && (
        <ClientConfigForm
          client={client}
          editingData={editingData}
          setEditingData={setEditingData}
          computePrice={computePrice}
          getSubscriptionStatus={getSubscriptionStatus}
          onApplyAddonsNow={onApplyAddonsNow}
        />
      )}
      {tab === "pagos" && (
        <ClientPayments
          editingData={editingData}
          paymentForm={paymentForm}
          setPaymentForm={setPaymentForm}
          payments={payments}
          computePrice={computePrice}
          onRegister={onRegisterPayment}
        />
      )}
    </Sheet>
  );
}
